import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { patchMaterial } from '../render/materials';
import type { SharedUniforms } from '../world/Environment';
import { mulberry32 } from '../world/noise';

function strawTexture(): { map: THREE.CanvasTexture; bump: THREE.CanvasTexture } {
  const w = 1024;
  const h = 256;
  const color = document.createElement('canvas');
  color.width = w;
  color.height = h;
  const bump = document.createElement('canvas');
  bump.width = w;
  bump.height = h;
  const c = color.getContext('2d');
  const b = bump.getContext('2d');
  if (!c || !b) throw new Error('2D canvas unavailable');
  const rng = mulberry32(21);
  // v runs from apex (0) to rim (1); u wraps around the hat.
  const grad = c.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#5a4121');
  grad.addColorStop(0.12, '#8a6a3a');
  grad.addColorStop(0.7, '#a7854f');
  grad.addColorStop(1, '#8f6f40');
  c.fillStyle = grad;
  c.fillRect(0, 0, w, h);
  b.fillStyle = '#808080';
  b.fillRect(0, 0, w, h);
  // Radiating straw fibres.
  for (let i = 0; i < 1400; i++) {
    const x = rng() * w;
    const y0 = rng() * h * 0.2;
    const len = h * (0.4 + rng() * 0.8);
    const tone = 90 + rng() * 90;
    c.strokeStyle = `rgba(${tone + 40},${tone + 15},${tone - 30},${0.25 + rng() * 0.35})`;
    c.lineWidth = 0.8 + rng() * 1.6;
    c.beginPath();
    c.moveTo(x, y0);
    c.lineTo(x + (rng() - 0.5) * 6, y0 + len);
    c.stroke();
    b.strokeStyle = `rgba(255,255,255,${0.15 + rng() * 0.25})`;
    b.lineWidth = 1;
    b.beginPath();
    b.moveTo(x, y0);
    b.lineTo(x, y0 + len);
    b.stroke();
  }
  // Concentric woven bands.
  for (let ring = 0; ring < 18; ring++) {
    const y = (ring / 18) * h + 6;
    c.fillStyle = `rgba(60,40,18,${0.18 + rng() * 0.12})`;
    c.fillRect(0, y, w, 1.6);
    b.fillStyle = 'rgba(0,0,0,0.35)';
    b.fillRect(0, y, w, 1.6);
    for (let x = 0; x < w; x += 8) {
      c.fillStyle = `rgba(200,165,110,${0.12 + rng() * 0.1})`;
      c.fillRect(x + (ring % 2) * 4, y - 2, 4, 2);
    }
  }
  // Binding ring near the rim.
  c.fillStyle = 'rgba(40,26,12,0.8)';
  c.fillRect(0, h * 0.965, w, h * 0.035);
  const map = new THREE.CanvasTexture(color);
  map.colorSpace = THREE.SRGBColorSpace;
  map.wrapS = THREE.RepeatWrapping;
  map.anisotropy = 8;
  const bumpMap = new THREE.CanvasTexture(bump);
  bumpMap.wrapS = THREE.RepeatWrapping;
  return { map, bump: bumpMap };
}

function tsukaTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 256;
  const c = canvas.getContext('2d');
  if (!c) throw new Error('2D canvas unavailable');
  c.fillStyle = '#d8ccb4';
  c.fillRect(0, 0, 64, 256);
  c.fillStyle = '#17120e';
  // Diamond ito wrap over rayskin.
  for (let y = -32; y < 256; y += 32) {
    c.beginPath();
    c.moveTo(0, y);
    c.lineTo(32, y + 16);
    c.lineTo(64, y);
    c.lineTo(64, y + 20);
    c.lineTo(32, y + 36);
    c.lineTo(0, y + 20);
    c.closePath();
    c.fill();
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  return texture;
}

/** Conical sugegasa straw hat. Origin at the crown contact point, +Y up. */
export function createKasa(uniforms: SharedUniforms): THREE.Group {
  const group = new THREE.Group();
  group.name = 'kasa';
  const { map, bump } = strawTexture();
  const material = patchMaterial(
    new THREE.MeshStandardMaterial({
      color: 0xffffff,
      map,
      bumpMap: bump,
      bumpScale: 1.2,
      roughness: 0.82,
      metalness: 0,
      side: THREE.DoubleSide,
      envMapIntensity: 0.8,
    }),
    uniforms,
    { rim: 0.55 },
  );
  const radius = 0.31;
  const height = 0.165;
  const top: THREE.Vector2[] = [];
  const steps = 18;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const r = t * radius;
    // Slightly concave cone with a small rounded knob at the apex.
    const y = height * Math.pow(1 - t, 1.18) + (t < 0.06 ? 0.006 * Math.cos((t / 0.06) * Math.PI * 0.5) : 0);
    top.push(new THREE.Vector2(Math.max(r, 0.0005), y));
  }
  const topGeo = new THREE.LatheGeometry(top, 72);
  const topMesh = new THREE.Mesh(topGeo, material);
  topMesh.castShadow = true;
  topMesh.receiveShadow = true;
  group.add(topMesh);

  const under: THREE.Vector2[] = top.map((p) => new THREE.Vector2(p.x * 0.985, p.y - 0.012));
  const underMat = patchMaterial(
    new THREE.MeshStandardMaterial({ color: 0x6d5431, map, roughness: 0.9, side: THREE.DoubleSide, envMapIntensity: 0.5 }),
    uniforms,
    { rim: 0.3 },
  );
  const underMesh = new THREE.Mesh(new THREE.LatheGeometry(under, 72), underMat);
  group.add(underMesh);

  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(radius * 0.995, 0.0065, 6, 96),
    patchMaterial(new THREE.MeshStandardMaterial({ color: 0x3a2a16, roughness: 0.8 }), uniforms, { rim: 0.4 }),
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = -0.004;
  rim.castShadow = true;
  group.add(rim);

  // Inner head band that visually seats the hat on the head.
  const band = new THREE.Mesh(
    new THREE.CylinderGeometry(0.085, 0.09, 0.05, 24, 1, true),
    patchMaterial(new THREE.MeshStandardMaterial({ color: 0x2b2118, roughness: 0.9, side: THREE.DoubleSide }), uniforms),
  );
  band.position.y = 0.02;
  group.add(band);
  return group;
}

/** Sword space: origin at the tsuba, +Y toward the kashira, -Y toward the tip, +Z the edge (convex side of the sori). */
export const SAYA_LENGTH = 0.74;
export const BLADE_LENGTH = 0.72;
/** Blade length measured from the tsuba face, including the habaki. */
export const BLADE_TIP = 0.004 + BLADE_LENGTH;
const SORI = 0.016;

/** Centreline offset toward the edge at `d` metres below the tsuba; saya and blade share it so the blade seats cleanly. */
export function soriAt(d: number): number {
  const u = Math.min(Math.max(d / SAYA_LENGTH, 0), 1);
  return SORI * 4 * u * (1 - u);
}

type KatanaMaterials = { lacquer: THREE.Material; iron: THREE.Material; brass: THREE.Material; wrap: THREE.Material; steel: THREE.Material };

let shared: KatanaMaterials | null = null;

function katanaMaterials(uniforms: SharedUniforms): KatanaMaterials {
  if (shared) return shared;
  const wrapTex = tsukaTexture();
  wrapTex.repeat.set(3, 3.5);
  const hamon = hamonTextures();
  shared = {
    lacquer: patchMaterial(
      new THREE.MeshPhysicalMaterial({ color: 0x0b0a0a, roughness: 0.3, clearcoat: 1, clearcoatRoughness: 0.18, envMapIntensity: 1.1 }),
      uniforms,
      { rim: 0.2 },
    ),
    iron: patchMaterial(new THREE.MeshStandardMaterial({ color: 0x2c2722, roughness: 0.45, metalness: 0.75, envMapIntensity: 1 }), uniforms),
    brass: patchMaterial(new THREE.MeshStandardMaterial({ color: 0x9b7a3d, roughness: 0.35, metalness: 0.9 }), uniforms),
    wrap: patchMaterial(new THREE.MeshStandardMaterial({ color: 0xffffff, map: wrapTex, roughness: 0.8 }), uniforms, { rim: 0.2 }),
    steel: patchMaterial(
      new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        map: hamon.color,
        roughnessMap: hamon.roughness,
        roughness: 1,
        metalness: 1,
        envMapIntensity: 1.3,
      }),
      uniforms,
      { rim: 0.12 },
    ),
  };
  return shared;
}

/** Hamon: frosted temper line along the edge over a mirror-polished ji. v = 0 at the shinogi, 1 at the edge. */
function hamonTextures(): { color: THREE.CanvasTexture; roughness: THREE.CanvasTexture } {
  const w = 512;
  const h = 64;
  const make = () => {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas unavailable');
    return { canvas, ctx };
  };
  const color = make();
  const rough = make();
  const rng = mulberry32(8);
  // Canvas row 0 is the edge (v = 1 after the default flipY).
  const boundary: number[] = [];
  for (let x = 0; x < w; x++) {
    const u = x / (w - 1);
    const gunome = Math.pow(Math.abs(Math.sin(u * 46 + Math.sin(u * 11) * 0.8)), 0.7);
    let b = 0.42 + gunome * 0.16 + Math.sin(u * 7.3) * 0.04;
    // Boshi: the temper line sweeps back toward the spine in the kissaki.
    b += Math.pow(Math.max(0, (u - 0.93) / 0.07), 1.5) * 0.4;
    // Hamachi: the hamon fades out just above the habaki.
    b *= 0.35 + 0.65 * Math.min(1, u / 0.05);
    boundary.push(b);
  }
  const jiColor = [150, 156, 164];
  const haColor = [226, 228, 231];
  for (let y = 0; y < h; y++) {
    const fromEdge = y / (h - 1);
    for (let x = 0; x < w; x++) {
      const b = boundary[x];
      // Nioi: a soft, slightly frosty transition band.
      const t = Math.min(1, Math.max(0, (b - fromEdge) / 0.09 + 0.5));
      const nie = rng() < 0.05 * t * (1 - t) * 4 ? 18 : 0;
      const edge = fromEdge < 0.05 ? 1 : 0;
      const c = jiColor.map((j, i) => Math.round(j + (haColor[i] - j) * t + nie + edge * 12));
      color.ctx.fillStyle = `rgb(${c[0]},${c[1]},${c[2]})`;
      color.ctx.fillRect(x, y, 1, 1);
      const r = Math.round(255 * (edge ? 0.2 : 0.13 + 0.22 * t));
      rough.ctx.fillStyle = `rgb(${r},${r},${r})`;
      rough.ctx.fillRect(x, y, 1, 1);
    }
  }
  const colorTex = new THREE.CanvasTexture(color.canvas);
  colorTex.colorSpace = THREE.SRGBColorSpace;
  colorTex.anisotropy = 8;
  const roughTex = new THREE.CanvasTexture(rough.canvas);
  roughTex.colorSpace = THREE.NoColorSpace;
  roughTex.anisotropy = 8;
  return { color: colorTex, roughness: roughTex };
}

/** Shinogi-zukuri blade: ridge line, iori-mune spine, curved edge meeting the spine at the kissaki. */
function bladeGeometry(): THREE.BufferGeometry {
  const start = 0.006;
  const tip = BLADE_TIP;
  const yokote = tip - 0.046;
  const stations: { d: number; w: number; t: number; spine: number }[] = [];
  const bodySteps = 22;
  const wBase = 0.031;
  const wYokote = 0.023;
  for (let i = 0; i <= bodySteps; i++) {
    const d = start + ((yokote - start) * i) / bodySteps;
    const k = (d - start) / (yokote - start);
    const w = wBase + (wYokote - wBase) * k;
    stations.push({ d, w, t: 0.0072 + (0.0052 - 0.0072) * k, spine: soriAt(d) - w / 2 });
  }
  const tipSteps = 9;
  const spineY = soriAt(yokote) - wYokote / 2;
  for (let i = 1; i <= tipSteps; i++) {
    const k = i / tipSteps;
    const d = yokote + (tip - yokote) * k;
    // Fukura: the edge rounds up to meet the spine line at the point.
    const w = wYokote * Math.sqrt(Math.max(0, 1 - k * k)) * (1 - 0.04 * k);
    const spine = spineY + (soriAt(d) - soriAt(yokote));
    stations.push({ d, w: Math.max(w, 0.0004), t: 0.0052 * Math.max(0.12, w / wYokote), spine });
  }
  // Cross-section (x across the flat, u from the spine toward the edge), counter-clockwise seen from the tip.
  const section = [
    [0, 0],
    [-0.3, 0.08],
    [-0.5, 0.3],
    [0, 1],
    [0.5, 0.3],
    [0.3, 0.08],
  ];
  const hira = new Set([2, 3]);
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const point = (s: (typeof stations)[number], j: number, out: THREE.Vector3) =>
    out.set(section[j][0] * s.t, -s.d, s.spine + section[j][1] * s.w);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const a2 = new THREE.Vector3();
  const along = new THREE.Vector3();
  const across = new THREE.Vector3();
  const n = new THREE.Vector3();
  const centre = new THREE.Vector3();
  for (let f = 0; f < section.length; f++) {
    const j0 = f;
    const j1 = (f + 1) % section.length;
    const base = positions.length / 3;
    stations.forEach((s, i) => {
      const next = stations[Math.min(i + 1, stations.length - 1)];
      const prev = stations[Math.max(i - 1, 0)];
      for (const [j, v] of [
        [j0, 0],
        [j1, 1],
      ] as const) {
        point(s, j, a);
        point(next, j, b);
        point(prev, j, a2);
        along.subVectors(b, a2).normalize();
        point(s, j1, b);
        point(s, j0, a2);
        across.subVectors(b, a2);
        n.crossVectors(across, along).normalize();
        centre.set(0, -s.d, s.spine + s.w * 0.35);
        if (n.dot(a.clone().sub(centre)) < 0) n.negate();
        positions.push(a.x, a.y, a.z);
        normals.push(n.x, n.y, n.z);
        // Hira facets carry the hamon; v runs from the shinogi (0) to the edge (1).
        const hv = hira.has(f) ? (f === 2 ? v : 1 - v) : 0;
        uvs.push(s.d / tip, hv);
      }
    });
    for (let i = 0; i < stations.length - 1; i++) {
      const p = base + i * 2;
      indices.push(p, p + 2, p + 1, p + 1, p + 2, p + 3);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  return geometry;
}

const baked = (geometry: THREE.BufferGeometry, position: THREE.Vector3Like, scale: THREE.Vector3Like = { x: 1, y: 1, z: 1 }) =>
  geometry.applyMatrix4(new THREE.Matrix4().compose(new THREE.Vector3().copy(position), new THREE.Quaternion(), new THREE.Vector3().copy(scale)));

const part = (geometries: THREE.BufferGeometry[], material: THREE.Material, name: string) => {
  const mesh = new THREE.Mesh(mergeGeometries(geometries.map((g) => (g.index ? g : g.toNonIndexed()))), material);
  mesh.name = name;
  mesh.castShadow = true;
  return mesh;
};

/** Lacquered saya with its iron fittings. Same frame as the sword, so a seated sword shares its transform. */
export function createSaya(uniforms: SharedUniforms): THREE.Group {
  const m = katanaMaterials(uniforms);
  const group = new THREE.Group();
  group.name = 'saya';
  const points: THREE.Vector3[] = [];
  for (let i = 0; i <= 8; i++) {
    const d = 0.012 + ((SAYA_LENGTH - 0.012) * i) / 8;
    points.push(new THREE.Vector3(0, -d, soriAt(d)));
  }
  const tube = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 28, 0.0155, 12, false);
  tube.scale(0.78, 1, 1);
  group.add(part([tube], m.lacquer, 'saya-body'));
  const kojiri = baked(new THREE.SphereGeometry(0.0158, 12, 8), { x: 0, y: -SAYA_LENGTH, z: soriAt(SAYA_LENGTH) }, { x: 0.78, y: 1.4, z: 1 });
  const koiguchi = baked(new THREE.CylinderGeometry(0.0165, 0.0165, 0.02, 14), { x: 0, y: -0.02, z: 0 }, { x: 0.78, y: 1, z: 1 });
  group.add(part([kojiri, koiguchi], m.iron, 'saya-fittings'));
  return group;
}

export type Sword = { group: THREE.Group; blade: THREE.Mesh };

/** Drawn katana: tsuba, fuchi, tsuka, kashira, habaki and the blade. */
export function createSword(uniforms: SharedUniforms): Sword {
  const m = katanaMaterials(uniforms);
  const group = new THREE.Group();
  group.name = 'katana';
  const tsubaProfile = [
    new THREE.Vector2(0.012, -0.004),
    new THREE.Vector2(0.035, -0.004),
    new THREE.Vector2(0.038, -0.0028),
    new THREE.Vector2(0.0385, 0),
    new THREE.Vector2(0.038, 0.0028),
    new THREE.Vector2(0.035, 0.004),
    new THREE.Vector2(0.012, 0.004),
  ];
  const tsuba = baked(new THREE.LatheGeometry(tsubaProfile, 32), { x: 0, y: 0, z: 0 }, { x: 0.82, y: 1, z: 1 });
  const kashira = baked(new THREE.CylinderGeometry(0.0146, 0.0142, 0.018, 14), { x: 0, y: 0.277, z: 0 }, { x: 0.8, y: 1, z: 1 });
  group.add(part([tsuba, kashira], m.iron, 'katana-iron'));
  const fuchi = baked(new THREE.CylinderGeometry(0.0134, 0.0144, 0.014, 14), { x: 0, y: 0.011, z: 0 }, { x: 0.8, y: 1, z: 1 });
  const habaki = baked(new THREE.CylinderGeometry(1, 1, 0.026, 14), { x: 0, y: -0.017, z: soriAt(0.017) }, { x: 0.0054, y: 1, z: 0.0165 });
  group.add(part([fuchi, habaki], m.brass, 'katana-brass'));
  const tsuka = baked(new THREE.CylinderGeometry(0.0138, 0.0152, 0.25, 14, 1), { x: 0, y: 0.143, z: 0 }, { x: 0.8, y: 1, z: 1 });
  group.add(part([tsuka], m.wrap, 'katana-tsuka'));
  const blade = new THREE.Mesh(bladeGeometry(), m.steel);
  blade.name = 'katana-blade';
  blade.castShadow = true;
  group.add(blade);
  return { group, blade };
}
